// packages/shared/src/services/UserProfileService.ts
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { FirebaseService } from './firebaseService';
import { PlayerRatings, EmailPreferences } from '../types';

export interface UserProfileUpdateData {
  name?: string;
  'preferences.ratings'?: PlayerRatings;
  'preferences.emailPreferences'?: EmailPreferences;
}

export class UserProfileService {
  private static instance: UserProfileService;

  private constructor() {}

  public static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService();
    }
    return UserProfileService.instance;
  }

  /**
   * Update user profile information (name, ratings, etc.)
   */
  async updateProfile(
    userId: string,
    data: UserProfileUpdateData
  ): Promise<void> {
    try {
      const db = FirebaseService.getInstance().getFirestore();
      const userRef = doc(db, 'users', userId);

      // Add updatedAt timestamp
      const updateData = {
        ...data,
        updatedAt: new Date()
      };

      await updateDoc(userRef, updateData);
      console.log('✅ UserProfileService: Profile updated successfully');
    } catch (error) {
      console.error('❌ UserProfileService: Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Update only the user's name
   */
  async updateName(userId: string, name: string): Promise<void> {
    return this.updateProfile(userId, { name });
  }

  /**
   * Update only the user's ratings
   */
  async updateRatings(userId: string, ratings: PlayerRatings): Promise<void> {
    return this.updateProfile(userId, { 'preferences.ratings': ratings });
  }

  /**
   * Update email preferences
   */
  async updateEmailPreferences(
    userId: string,
    emailPreferences: EmailPreferences
  ): Promise<void> {
    try {
      const db = FirebaseService.getInstance().getFirestore();
      const userRef = doc(db, 'users', userId);

      await updateDoc(userRef, {
        'preferences.emailPreferences': emailPreferences,
        updatedAt: new Date()
      });

      console.log('✅ UserProfileService: Email preferences updated successfully');
    } catch (error) {
      console.error('❌ UserProfileService: Error updating email preferences:', error);
      throw error;
    }
  }

  /**
   * Get user's current email preferences
   */
  async getEmailPreferences(userId: string): Promise<EmailPreferences | null> {
    try {
      const db = FirebaseService.getInstance().getFirestore();
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.warn('⚠️ UserProfileService: User document not found');
        return null;
      }

      const userData = userDoc.data();
      return userData?.preferences?.emailPreferences || null;
    } catch (error) {
      console.error('❌ UserProfileService: Error getting email preferences:', error);
      throw error;
    }
  }

  /**
   * Update user profile with name and ratings together
   * This is optimized for the account settings page
   */
  async updateNameAndRatings(
    userId: string,
    name: string,
    ratings: PlayerRatings
  ): Promise<void> {
    return this.updateProfile(userId, {
      name,
      'preferences.ratings': ratings
    });
  }
}